import {
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import moment from "moment";
import React from "react";
import NumberFormat from "react-number-format";
import { DoneIcon } from "./StepIcons";
import TransactionHistoryBase from "./TransactionHistoryBase";
import styles from "./styles";
import {
  decideTransactionMethod,
  getCompletedRelated,
  isCorrectionsExist,
} from "./utils";
const liveAgentWhatsappNumber = process.env.REACT_APP_LIVE_AGENT_WHATSAPP_NUMBER?.trim();

const ManualAdjustment = ({ details, amount, fee }) => {
  return (
    <Stepper orientation="vertical" style={{ padding: 0 }}>
      {details[0].adjustments
        .reverse()
        .map(({ completeUnit, completeUnitPrice, navDate }, index) => (
          <Step active key={index}>
            <StepLabel icon={<DoneIcon active />}>
              <Typography
                color="textPrimary"
                style={{
                  ...styles.baseFont,
                  ...styles.label,
                }}
              >
                Unit Price Adjustment
              </Typography>
            </StepLabel>
            <StepContent>
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                }}
              >
                Unit Price is adjusted to reflect the fund's value.
              </Typography>
              <div
                style={{
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  Unit: {completeUnit}
                </Typography>

                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  Unit Price: RM
                  <NumberFormat
                    value={parseFloat(completeUnitPrice)}
                    thousandSeparator
                    displayType="text"
                    decimalScale={4}
                    fixedDecimalScale
                  />
                </Typography>
              </div>
              <div
                style={{
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  Request Cash out (RM):
                </Typography>
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  RM {(amount - fee).toFixed(2)}
                </Typography>
              </div>

              <div
                style={{
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  Adjusted Cash out (RM):
                </Typography>
                <Typography
                  style={{
                    ...styles.baseFont,
                    ...styles.content,
                  }}
                >
                  RM
                  {Number(details[0].adjustmentAmount - fee).toFixed(2)}
                </Typography>
              </div>
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                  ...styles.date,
                  marginTop: "8px",
                }}
              >
                {moment(navDate)
                  .utcOffset("+0800")
                  .format("DD MMM YYYY")}
              </Typography>
            </StepContent>
          </Step>
        ))}
    </Stepper>
  );
};

const Sell = ({
  status,
  method,
  amount,
  unit,
  fee,
  submittedDate,
  processingDate,
  completedDate,
  details,
  isFullSell,
  isManSell,
  planId,
  amountOnly,
  isSell,
  cashOutamtSell,
  compUnitPrice
}) => {
  const isRetire = planId === "P11";

  const successText = isRetire
    ? `Your ${
        isFullSell ? "Full" : ""
      } Cash Out amount is successful and has been reflected in your account`
    : isFullSell
    ? "Full Cash Out has been initiated due to the balance of units are below the minimum unit holding balance threshold."
    : `Your Cash Out payment is successful and has been reflected in your account`;

  const isSubmitted =
    status === "3" ||
    status === "4" ||
    status === "5" ||
    status === "6" ||
    status === "9" ||
    status === "10" ||
    status === "11";

  const isProcessing =
    status === "4" ||
    status === "5" ||
    status === "9" ||
    status === "10" ||
    status === "11";

  const isCompleted = status === "9" || status === "10" || status === "11";

  const corrections = isCorrectionsExist(details);

  const corrsell =
    Array.isArray(corrections) &&
    corrections.every(
      item =>
        item.hasOwnProperty("reversal") && item.hasOwnProperty("correction")
    );

  const corrsellWithPayment = corrsell && status === "10";
  const corrsellWithoutPayment = corrsell && status === "5";

  const reversalWithoutPayment =
    Array.isArray(corrections) &&
    corrections.every(
      item =>
        item.hasOwnProperty("reversal") && !item.hasOwnProperty("correction")
    ) &&
    status === "5";

  const completed = getCompletedRelated({
    status,
    isDoneIconActive: status === "10" || corrsellWithPayment,
  });

  return (
    <TransactionHistoryBase
      manualAdjustment={
        details.length > 0 && details[0].adjustments.length > 0 ? (
          <ManualAdjustment details={details} amount={amount} fee={fee} />
        ) : null
      }
    >
      {/* Submitted */}
      <Step active={isSubmitted}>
        <StepLabel
          icon={<DoneIcon active={isSubmitted} />}
          sx={{ padding: "0" }}
        >
          <Typography
            color="textPrimary"
            style={{ ...styles.baseFont, ...styles.label }}
          >
            Submitted
          </Typography>
        </StepLabel>
        <StepContent
          style={{
            borderColor: isSubmitted && "#0091DA",
            borderWidth: 2,
            marginTop: "0",
            height: "100%",
            paddingBottom: "24px",
          }}
        >
          <Typography style={{ ...styles.baseFont, ...styles.content }}>
            Your Cash Out amount has been successfully submitted
          </Typography>
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              marginTop: "8px",
            }}
          >
            Transaction Method:{" "}
            {decideTransactionMethod({ transactionType: "Sell", method })}
          </Typography>

          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              ...styles.date,
              marginTop: "8px",
            }}
          >
            {moment(submittedDate)
              .utcOffset("+0800")
              .format(`DD MMM YYYY${isManSell || isRetire ? "" : ", h:mm a"}`)}
          </Typography>
        </StepContent>
      </Step>

      {/* Processing */}
      <Step active={isProcessing}>
        <StepLabel
          icon={<DoneIcon active={isProcessing} />}
          sx={{ padding: "0" }}
        >
          <Typography
            color="textPrimary"
            style={{
              ...styles.baseFont,
              ...styles.label,
              color: isProcessing ? "#000000" : "#9D9EA4",
            }}
          >
            Processing
          </Typography>
        </StepLabel>
        <StepContent
          style={{
            borderColor: isProcessing && "#0091DA",
            borderWidth: 2,
            height: "100%",
            marginTop: "0",
            paddingBottom: "24px",
          }}
        >
          {(isRetire || isManSell) && (
            <Typography style={{ ...styles.baseFont, ...styles.content }}>
              Your Cash Out {isRetire ? "amount" : ""} is being processed
            </Typography>
          )}
          <Typography
            style={{
              ...styles.baseFont,
              ...styles.content,
              ...styles.date,
              marginTop: "8px",
            }}
          >
            {isManSell ? (
              submittedDate !== null &&
              moment(submittedDate)
                .utcOffset("+0800")
                .format(
                  "DD MMM YYYY"
                )
            ) : (
              processingDate !== null &&
              moment(processingDate)
                .utcOffset("+0800")
                .format(
                  `DD MMM YYYY${isManSell || isRetire ? "" : ", h:mm a"}`
                )
            )}
          </Typography>
        </StepContent>
      </Step>

      {/* Completed */}
      {!reversalWithoutPayment && !corrsellWithoutPayment && (
        <Step
          active={isCompleted || corrsellWithPayment}
          style={{
            paddingBottom:
              details.length > 0 && details[0].adjustments.length > 0 && "24px",
          }}
        >
          <StepLabel icon={completed.icon} sx={{ padding: "0" }}>
            <Typography
              color="textPrimary"
              style={{
                ...styles.baseFont,
                ...styles.label,
                color:
                  status === "9" ||
                  status === "10" ||
                  status === "11" ||
                  corrsellWithPayment
                    ? "#000000"
                    : "#9D9EA4",
              }}
            >
              {completed.text}
            </Typography>
          </StepLabel>
          <StepContent
            style={{
              borderColor: (isCompleted || corrsellWithPayment) && "#0091DA",
              borderWidth: 2,
              marginTop: "0",
              height: "100%",
            }}
          >
            <Typography style={{ ...styles.baseFont, ...styles.content }}>
              {status === "9" && "Your Cash Out is unsuccessful"}
              {!isSell && status === "10" && successText}
              {status === "11" && (
                <>
                  invalid account number, please update your bank account with
                  our{" "}
                  <span
                    onClick={() =>
                      window.my.call("openAppLink", {
                        url: `https://wa.me/${liveAgentWhatsappNumber}`,
                      })
                    }
                    style={{ color: "#0076CF" }}
                  >
                    live agent on WhatsApp
                  </span>
                </>
              )}
            </Typography>
            {isSell && corrsellWithPayment && (
              <div
                style={{
                  marginTop: "8px",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography style={{ ...styles.baseFont, ...styles.content }}>
                  Unit: {unit}
                </Typography>
                <Typography style={{ ...styles.baseFont, ...styles.content }}>
                  Unit Price: RM
                  <NumberFormat
                    value={
                      details.length > 0 &&
                      (details[0].actualUnitPrice || parseFloat(amount / unit))
                    }
                    thousandSeparator
                    displayType="text"
                    decimalScale={4}
                    fixedDecimalScale
                  />
                </Typography>
              </div>
            )}
            {status === "10" && (
              <>
                <div
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography style={{ ...styles.baseFont, ...styles.content }}>
                    Total Cash Out
                  </Typography>
                  <Typography style={{ ...styles.baseFont, ...styles.content }}>
                    RM
                    <NumberFormat
                      value={amountOnly}
                      thousandSeparator
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                    />
                  </Typography>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography style={{ ...styles.baseFont, ...styles.content }}>
                    Administration Fee
                  </Typography>
                  <Typography style={{ ...styles.baseFont, ...styles.content }}>
                    RM
                    <NumberFormat
                      value={fee}
                      thousandSeparator
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                    />
                  </Typography>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      fontWeight: 700,
                    }}
                  >
                    Net Amount
                  </Typography>
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      fontWeight: 700,
                    }}
                  >
                    RM
                    <NumberFormat
                      value={cashOutamtSell - fee}
                      thousandSeparator
                      displayType="text"
                      decimalScale={2}
                      fixedDecimalScale
                    />
                  </Typography>
                </div>
                <div
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography style={{ ...styles.baseFont, ...styles.content }}>
                    Unit: {unit}
                  </Typography>
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      textAlign: "right",
                    }}
                  >
                    Unit Price: RM
                    <NumberFormat
                      value={
                        details.length > 0 ?
                        (details[0].actualUnitPrice || parseFloat(amount / unit)) : compUnitPrice
                      }
                      thousandSeparator
                      displayType="text"
                      decimalScale={4}
                      fixedDecimalScale
                    />
                  </Typography>
                </div>
              </>
            )}

            {(status === "9" ||
              status === "10" ||
              status === "11" ||
              corrsellWithPayment) && (
              <Typography
                style={{
                  ...styles.baseFont,
                  ...styles.content,
                  ...styles.date,
                  marginTop: "8px",
                }}
              >
                {moment(completedDate)
                  .utcOffset("+0800")
                  .format("DD MMM YYYY")}
              </Typography>
            )}
          </StepContent>
        </Step>
      )}

      {Array.isArray(corrections) &&
        corrections.map((item, id) => (
          <React.Fragment key={id}>
            {item.reversal && (
              <Step active>
                <StepLabel icon={<DoneIcon active />} sx={{ padding: "0" }}>
                  <Typography
                    color="textPrimary"
                    style={{
                      ...styles.baseFont,
                      ...styles.label,
                      color: "#000000",
                    }}
                  >
                    Reverse Cash Out
                  </Typography>
                </StepLabel>
                <StepContent
                  style={
                    item.hasOwnProperty("correction")
                      ? {
                          borderColor: "#0091DA",
                          borderWidth: 2,
                          marginTop: "0",
                          height: "100%",
                          paddingBottom: "24px",
                        }
                      : { border: "none" }
                  }
                >
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      ...styles.date,
                      marginTop: "8px",
                    }}
                  >
                    {moment(item.reversal.navDate)
                      .utcOffset("+0800")
                      .format("DD MMM YYYY")}
                  </Typography>
                </StepContent>
              </Step>
            )}
            {item.correction && (
              <Step active>
                <StepLabel icon={<DoneIcon active />} sx={{ padding: "0" }}>
                  <Typography
                    color="textPrimary"
                    style={{
                      ...styles.baseFont,
                      ...styles.label,
                      color: "#000000",
                    }}
                  >
                    Adjusted Cash Out
                  </Typography>
                </StepLabel>
                <StepContent
                  style={
                    id !== corrections.length - 1
                      ? {
                          borderColor: "#0091DA",
                          borderWidth: "2px",
                          height: "100%",
                          marginTop: "0",
                          paddingBottom: "24px",
                        }
                      : { border: "none" }
                  }
                >
                  <div
                    style={{
                      marginTop: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      Request Cash Out
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      RM{" "}
                      <NumberFormat
                        value={cashOutamtSell}
                        thousandSeparator
                        displayType="text"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  <div
                    style={{
                      marginTop: "3px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      Adjusted Cash Out
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                      }}
                    >
                      RM{" "}
                      <NumberFormat
                        value={
                          item.correction.actualAmt - item.correction.feeAmount
                        }
                        thousandSeparator
                        displayType="text"
                        decimalScale={2}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>

                  <div
                    style={{
                      marginTop: "8px",
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      style={{ ...styles.baseFont, ...styles.content }}
                    >
                      Unit: {item.correction.unit}
                    </Typography>
                    <Typography
                      style={{
                        ...styles.baseFont,
                        ...styles.content,
                        textAlign: "right",
                      }}
                    >
                      Unit Price: RM
                      <NumberFormat
                        value={
                          item.correction.actualUnitPrice ||
                          parseFloat(
                            item.correction.amount / item.correction.unit
                          )
                        }
                        thousandSeparator
                        displayType="text"
                        decimalScale={4}
                        fixedDecimalScale
                      />
                    </Typography>
                  </div>
                  <Typography
                    style={{
                      ...styles.baseFont,
                      ...styles.content,
                      ...styles.date,
                      marginTop: "8px",
                    }}
                  >
                    {moment(item.correction.navDate)
                      .utcOffset("+0800")
                      .format("DD MMM YYYY")}
                  </Typography>
                </StepContent>
              </Step>
            )}
          </React.Fragment>
        ))}
    </TransactionHistoryBase>
  );
};

export default Sell;
